/** REACT_APP_USE_VERSION_POLICY=true → CheckClientVersion, 그 외 → REACT_APP_USE_URL 만 */
import { Capacitor } from '@capacitor/core';
import Commons from './Common';
import Global from './Global';
import { logApiUrl } from './logApiUrl';

/** 스토어 빌드와 맞출 것 (android/app/build.gradle, ios MARKETING_VERSION) */
const APP_RELEASE = {
  IOS: { currentVersion: 'v1.4.1', buildNo: '' },
  ANDROID: { currentVersion: 'v1.4.1', buildNo: '' },
};

/** 브라우저(macStart)에서 테스트할 플랫폼 — IOS | ANDROID */
const DEV_PLATFORM = 'ANDROID';

let policy = null;

export const getClientVersionPolicy = () => policy;

export const shouldShowUpdateDialog = () => {
  if (!Capacitor.isNativePlatform()) return false;
  if (process.env.REACT_APP_USE_VERSION_POLICY !== 'true' || !policy)
    return false;
  if (policy.forceUpdate) return true;

  const cur = Commons.Storages('get', 'version')?.version || '';
  const n = (v) =>
    String(v || '')
      .replace(/^v/i, '')
      .split('.')
      .map((x) => +x || 0);
  const cmp = (a, b) => {
    const x = n(a);
    const y = n(b);
    for (let i = 0; i < Math.max(x.length, y.length); i++) {
      if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) - (y[i] ?? 0);
    }
    return 0;
  };

  if (policy.minVersion && cmp(cur, policy.minVersion) < 0) return true;
  if (policy.latestVersion && cmp(cur, policy.latestVersion) < 0) return true;
  return false;
};

export async function initClientVersionPolicy() {
  logApiUrl('init 시작', {
    USE_VERSION_POLICY: process.env.REACT_APP_USE_VERSION_POLICY,
    env_USE_URL: process.env.REACT_APP_USE_URL,
    globalInit: Global.base.useUrl,
  });

  const cap = Capacitor.getPlatform();
  const platform =
    cap === 'ios' ? 'IOS' : cap === 'android' ? 'ANDROID' : DEV_PLATFORM;
  const { currentVersion, buildNo } =
    APP_RELEASE[platform] || APP_RELEASE.ANDROID;

  Commons.Storages('set', 'version', { version: currentVersion });

  let base = (process.env.REACT_APP_USE_URL || '').trim();
  if (base && !base.endsWith('/')) base += '/';

  if (process.env.REACT_APP_USE_VERSION_POLICY !== 'true') {
    policy = null;
    Commons.Storages('remove', 'clientVersionPolicy');
    if (base) Global.base.useUrl = base;
    logApiUrl('버전정책 OFF', { finalBaseUrl: Global.base.useUrl });
    return null;
  }

  if (!base) {
    console.warn('[ApiUrl] REACT_APP_USE_URL 없음');
    return null;
  }

  const checkUrl = `${base}${Global.urls.Account.CheckClientVersion}`;
  logApiUrl('CheckClientVersion 요청', {
    checkUrl,
    platform,
    currentVersion,
    buildNo,
    baseBefore: Global.base.useUrl,
  });

  try {
    const res = await Commons.Axios('post', checkUrl, {
      platform,
      currentVersion,
      buildNo,
    });

    const body = res?.data ?? res;
    if (body?.type === 'Error') throw new Error(body.message);

    const row = body.data?.[0];
    if (!row) throw new Error('CheckClientVersion 응답 없음');

    let apiUrl = (row.apiBaseUrl || '').trim();
    if (apiUrl && !apiUrl.endsWith('/')) apiUrl += '/';

    logApiUrl('CheckClientVersion 응답', {
      responseApiBaseUrl: row.apiBaseUrl,
      forceUpdate: row.forceUpdate,
      latestVersion: row.latestVersion,
      minVersion: row.minVersion,
      baseBefore: Global.base.useUrl,
    });

    if (apiUrl) Global.base.useUrl = apiUrl;

    policy = {
      latestVersion: row.latestVersion,
      minVersion: row.minVersion,
      forceUpdate: !!row.forceUpdate,
      storeUrl: row.storeUrl || '',
      apiBaseUrl: apiUrl,
    };
    Commons.Storages('set', 'clientVersionPolicy', policy);

    logApiUrl('버전정책 ON 완료', {
      finalBaseUrl: Global.base.useUrl,
      policyApiBaseUrl: policy.apiBaseUrl,
    });
  } catch (e) {
    console.warn('[ApiUrl] CheckClientVersion 실패', e?.message || e);
    const cached = Commons.Storages('get', 'clientVersionPolicy');
    if (cached?.apiBaseUrl) {
      Global.base.useUrl = cached.apiBaseUrl.endsWith('/')
        ? cached.apiBaseUrl
        : `${cached.apiBaseUrl}/`;
      policy = cached;
      logApiUrl('캐시 policy 적용', {
        cachedApiBaseUrl: cached.apiBaseUrl,
        finalBaseUrl: Global.base.useUrl,
      });
    } else {
      logApiUrl('실패 후 baseUrl 유지', { finalBaseUrl: Global.base.useUrl });
    }
  }

  return policy;
}
